// frontend/src/pages/Notifications.js – NextScenes Notifications page
// Build: 2026-04-28-notifications-page-v5-ns-hero-identity
//
// Purpose:
// - Show the logged-in user's system notifications.
// - Allow marking one notification or all notifications as read.
// - Keep the first frontend notification layer simple and safe.
// - Support EN/FR labels for the beta interface.

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";
import PushNotificationSettings from "../components/PushNotificationSettings";

const TEXT = {
  en: {
    pageTitle: "Notifications",
    pageSubtitle: "System updates about proposals, group activity, comments, and story decisions.",
    showAll: "Show all",
    showUnread: "Show unread",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    markAllRead: "Mark all read",
    loading: "Loading notifications…",
    empty: "No notifications yet. When a proposal or important story event happens, it will appear here.",
    noUnread: "No unread notifications",
    unreadOne: "1 unread notification",
    unreadMany: "{count} unread notifications",
    allReadNotice: "All notifications marked as read.",
    loadError: "Could not load notifications.",
    updateError: "Could not update notification.",
    markAllError: "Could not mark all notifications as read.",
    open: "Open",
    markRead: "Mark read",
    newLabel: "New",
    notification: "Notification",
    justNow: "Just now",
    minuteAgo: "1 minute ago",
    minutesAgo: "{count} minutes ago",
    hourAgo: "1 hour ago",
    hoursAgo: "{count} hours ago",
    userFallback: "A user",
    storyFallback: "this story",
    type: {
      proposal_submitted: "Proposal submitted",
      proposal_approved: "Proposal approved",
      proposal_rejected: "Proposal rejected",
      illustration_rejected: "Illustration rejected",
      proposal_round_opened: "Proposal round opened",
      comment_received: "Comment received",
      reply_received: "Reply received",
      group_invite: "Group invite",
      group_added: "Group update",
      story_updated: "Story update",
      scene_published: "New canon scene",
      system: "Notification",
    },
    title: {
      proposal_submitted: "New proposed scene submitted",
      proposal_approved: "Proposal approved",
      proposal_rejected: "Proposal rejected",
      illustration_rejected: "Illustration rejected",
      proposal_round_opened: "Proposal round opened",
      comment_received: "New comment received",
      reply_received: "New reply received",
      group_invite: "Group invitation",
      group_added: "Group update",
      story_updated: "Story update",
      scene_published: "New canon scene available",
      system: "Notification",
    },
    message: {
      proposal_submitted: "{actor} submitted a proposed scene for \"{story}\".",
      proposal_approved: "Your proposal for \"{story}\" was approved.",
      proposal_rejected: "Your proposal for \"{story}\" was rejected.",
      illustration_rejected: "Your scene illustration for \"{story}\" was rejected.",
      proposal_round_opened: "A proposal round was opened for \"{story}\".",
      comment_received: "A new comment was posted on \"{story}\".",
      reply_received: "A new reply was posted on \"{story}\".",
      group_invite: "You have been invited to join \"{story}\".",
      group_added: "You have been added to \"{story}\".",
      story_updated: "\"{story}\" was updated.",
      scene_published: "A new canon scene is available in \"{story}\".",
      scene_published_with_title: "\"{scene}\" is now available in \"{story}\".",
      system: "You have a new notification.",
    },
  },
  fr: {
    pageTitle: "Notifications",
    pageSubtitle: "Mises à jour du système concernant les propositions, les activités de groupe, les commentaires et les décisions liées aux histoires.",
    showAll: "Tout afficher",
    showUnread: "Afficher les non lues",
    refresh: "Actualiser",
    refreshing: "Actualisation…",
    markAllRead: "Tout marquer comme lu",
    loading: "Chargement des notifications…",
    empty: "Aucune notification pour le moment. Lorsqu’une proposition ou un événement important se produira, il apparaîtra ici.",
    noUnread: "Aucune notification non lue",
    unreadOne: "1 notification non lue",
    unreadMany: "{count} notifications non lues",
    allReadNotice: "Toutes les notifications ont été marquées comme lues.",
    loadError: "Impossible de charger les notifications.",
    updateError: "Impossible de mettre à jour la notification.",
    markAllError: "Impossible de marquer toutes les notifications comme lues.",
    open: "Ouvrir",
    markRead: "Marquer comme lu",
    newLabel: "Nouveau",
    notification: "Notification",
    justNow: "À l’instant",
    minuteAgo: "Il y a 1 minute",
    minutesAgo: "Il y a {count} minutes",
    hourAgo: "Il y a 1 heure",
    hoursAgo: "Il y a {count} heures",
    userFallback: "Un utilisateur",
    storyFallback: "cette histoire",
    type: {
      proposal_submitted: "Proposition soumise",
      proposal_approved: "Proposition approuvée",
      proposal_rejected: "Proposition rejetée",
      illustration_rejected: "Illustration rejetée",
      proposal_round_opened: "Phase de propositions ouverte",
      comment_received: "Commentaire reçu",
      reply_received: "Réponse reçue",
      group_invite: "Invitation de groupe",
      group_added: "Mise à jour du groupe",
      story_updated: "Mise à jour de l’histoire",
      scene_published: "Nouvelle scène canon",
      system: "Notification",
    },
    title: {
      proposal_submitted: "Nouvelle scène proposée soumise",
      proposal_approved: "Proposition approuvée",
      proposal_rejected: "Proposition rejetée",
      illustration_rejected: "Illustration rejetée",
      proposal_round_opened: "Phase de propositions ouverte",
      comment_received: "Nouveau commentaire reçu",
      reply_received: "Nouvelle réponse reçue",
      group_invite: "Invitation de groupe",
      group_added: "Mise à jour du groupe",
      story_updated: "Mise à jour de l’histoire",
      scene_published: "Nouvelle scène canon disponible",
      system: "Notification",
    },
    message: {
      proposal_submitted: "{actor} a soumis une scène proposée pour « {story} ».",
      proposal_approved: "Votre proposition pour « {story} » a été approuvée.",
      proposal_rejected: "Votre proposition pour « {story} » a été rejetée.",
      illustration_rejected: "Votre illustration pour « {story} » a été rejetée.",
      proposal_round_opened: "Une phase de propositions a été ouverte pour « {story} ».",
      comment_received: "Un nouveau commentaire a été publié sur « {story} ».",
      reply_received: "Une nouvelle réponse a été publiée sur « {story} ».",
      group_invite: "Vous avez été invité à rejoindre « {story} ».",
      group_added: "Vous avez été ajouté à « {story} ».",
      story_updated: "« {story} » a été mise à jour.",
      scene_published: "Une nouvelle scène canon est disponible dans « {story} ».",
      scene_published_with_title: "« {scene} » est maintenant disponible dans « {story} ».",
      system: "Vous avez une nouvelle notification.",
    },
  },
};

function getText(lang) {
  return TEXT[String(lang || "en").toLowerCase()] || TEXT.en;
}

function replaceVars(template, vars) {
  return String(template || "").replace(/\{(actor|story|scene|count)\}/g, (_, key) => String(vars?.[key] ?? ""));
}

function formatWhen(value, t, lang) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const diff = Date.now() - d.getTime();

  if (diff >= 0 && diff < 60 * 1000) return t.justNow;
  if (diff >= 0 && diff < 60 * 60 * 1000) {
    const mins = Math.max(1, Math.floor(diff / (60 * 1000)));
    return mins === 1 ? t.minuteAgo : replaceVars(t.minutesAgo, { count: mins });
  }
  if (diff >= 0 && diff < 24 * 60 * 60 * 1000) {
    const hrs = Math.max(1, Math.floor(diff / (60 * 60 * 1000)));
    return hrs === 1 ? t.hourAgo : replaceVars(t.hoursAgo, { count: hrs });
  }

  return d.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
}

function typeLabel(type, t) {
  const key = String(type || "system").trim();
  return t.type[key] || t.notification;
}

function titleLabel(item, t) {
  const key = String(item?.type || "system").trim();
  return t.title[key] || item?.title || t.notification;
}

function messageLabel(item, t) {
  const key = String(item?.type || "system").trim();

  // For detailed moderation messages, prefer the server-authored text.
  // It may include the rejection reason and the author's note.
  if (key === "illustration_rejected" && String(item?.message || "").trim()) {
    return String(item.message).trim();
  }

  const actor = String(item?.actorName || "").trim() || t.userFallback;
  const story = String(item?.storyTitle || "").trim() || t.storyFallback;
  const scene = String(item?.metadata?.sceneTitle || item?.sceneTitle || "").trim();

  const template =
    key === "scene_published" && scene
      ? t.message.scene_published_with_title
      : t.message[key];

  if (!template) return item?.message || "";
  return replaceVars(template, { actor, story, scene });
}

function resolveInternalTarget(link, item = null) {
  const raw = String(link || "").trim();
  if (!raw || !raw.startsWith("/")) return null;

  try {
    const url = new URL(raw, window.location.origin);
    const path = String(url.pathname || "").toLowerCase();
    const storyId = String(url.searchParams.get("storyId") || item?.storyId || "").trim();
    const sceneId = String(url.searchParams.get("sceneId") || item?.sceneId || "").trim();
    const openRaw = String(url.searchParams.get("openValidated") || url.searchParams.get("open") || "").trim().toLowerCase();
    const openValidated =
      openRaw === "1" ||
      openRaw === "true" ||
      String(item?.type || "").trim() === "scene_published";

    if (storyId) {
      try {
        window.localStorage.setItem("ns.selectedStoryId", storyId);
        window.localStorage.setItem("ns.writer.focusStoryId", storyId);
        window.localStorage.setItem("ns.reader.focusStoryId", storyId);
        window.localStorage.setItem("ns.reader.lastStoryId", storyId);
      } catch {}
    }

    if (path === "/writer-studio" || path === "/writerstudio") return { page: "writerStudio", storyId };
    if (path === "/story-dashboard" || path === "/storydashboard") return { page: "storyDashboard", storyId };
    if (path === "/reader-view" || path === "/readerview" || path === "/reader") {
      return { page: "readerView", storyId, sceneId, openValidated };
    }
    if (path === "/storylines") return { page: "storylines", storyId };
    if (path === "/storyhub" || path === "/story-hub") return { page: "storyHub", storyId };
  } catch {}

  return null;
}

function notificationIcon(type) {
  const key = String(type || "system").trim();
  const icons = {
    proposal_submitted: "✍️",
    proposal_approved: "✓",
    proposal_rejected: "×",
    proposal_round_opened: "◷",
    comment_received: "💬",
    reply_received: "↩",
    group_invite: "👥",
    group_added: "👥",
    story_updated: "◆",
    scene_published: "📖",
    system: "•",
  };
  return icons[key] || icons.system;
}

export default function Notifications({ onNavigatePage, onNotificationsChanged, lang = "en" }) {
  const t = getText(lang);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("all");

  const unreadCount = useMemo(() => items.filter((item) => !item?.isRead).length, [items]);

  async function syncAppBadge() {
    try {
      const data = await api.get("/api/push/badge-count");
      const count = Math.max(0, Number(data?.badgeCount || 0));

      if ("setAppBadge" in navigator) {
        if (count > 0) {
          await navigator.setAppBadge(count);
        } else if ("clearAppBadge" in navigator) {
          await navigator.clearAppBadge();
        }
      }

      return count;
    } catch {
      return null;
    }
  }

  async function loadNotifications() {
    setBusy(true);
    setErr("");
    setNotice("");

    try {
      const data = await api.getNotifications({ limit: 50, unreadOnly: filter === "unread" });
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      await syncAppBadge();
      if (typeof onNotificationsChanged === "function") onNotificationsChanged();
    } catch (e) {
      setErr(e?.message || t.loadError);
      setItems([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, lang]);

  useEffect(() => {
    function refreshWhenVisible() {
      if (document.visibilityState === "visible") {
        void loadNotifications();
      }
    }

    function handleServiceWorkerMessage(event) {
      if (event?.data?.type === "NEXTSCENES_PUSH_RECEIVED") {
        void loadNotifications();
      }
    }

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage
      );
    }

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage
        );
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, lang]);

  async function markOneRead(id) {
    const nid = String(id || "").trim();
    if (!nid) return;

    setErr("");
    setNotice("");

    try {
      await api.markNotificationRead(nid);
      setItems((rows) => {
        const nextRows = rows.map((row) =>
          String(row?.id || "") === nid
            ? { ...row, isRead: true, readAt: new Date().toISOString() }
            : row
        );
        return filter === "unread" ? nextRows.filter((row) => !row?.isRead) : nextRows;
      });
      await syncAppBadge();
      if (typeof onNotificationsChanged === "function") onNotificationsChanged();
    } catch (e) {
      setErr(e?.message || t.updateError);
    }
  }

  async function markAllRead() {
    setErr("");
    setNotice("");

    try {
      await api.markAllNotificationsRead();
      setItems((rows) => rows.map((row) => ({ ...row, isRead: true, readAt: row?.readAt || new Date().toISOString() })));
      setNotice(t.allReadNotice);
      if ("clearAppBadge" in navigator) {
        try {
          await navigator.clearAppBadge();
        } catch {}
      }
      if (typeof onNotificationsChanged === "function") onNotificationsChanged();
    } catch (e) {
      setErr(e?.message || t.markAllError);
    }
  }

  async function openNotification(item) {
    if (!item) return;
    if (!item.isRead) await markOneRead(item.id);

    const target = resolveInternalTarget(item.link, item);
    if (target && typeof onNavigatePage === "function") {
      const options = target.storyId
        ? {
            storyId: target.storyId,
            sceneId: target.sceneId || "",
            openValidated: target.openValidated === true,
          }
        : undefined;
      onNavigatePage(target.page, options);
    }
  }

  const unreadText = unreadCount > 0 ? (unreadCount === 1 ? t.unreadOne : replaceVars(t.unreadMany, { count: unreadCount })) : t.noUnread;

  return (
    <div className="page ns-notifications-page">
      <section className="ns-notifications-shell" aria-label={t.pageTitle}>
        <div
          className="ns-notifications-hero ns-identity-hero"
          style={{
            backgroundImage:
              'linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("/images/storyhub/storyhub-background.png")',
          }}
        >
          <div className="ns-notifications-hero-copy ns-identity-hero-copy">
            <div className="ns-notifications-kicker ns-identity-kicker">NextScenes</div>
            <h2 className="ns-notifications-title ns-identity-title">{t.pageTitle}</h2>
            <p className="ns-notifications-subtitle ns-identity-subtitle">{t.pageSubtitle}</p>
          </div>

          <div className="ns-notifications-summary ns-identity-hero-stat" aria-live="polite">
            <span className={unreadCount > 0 ? "ns-notifications-count ns-identity-stat-number is-unread" : "ns-notifications-count ns-identity-stat-number"}>
              {unreadCount}
            </span>
            <span className="ns-notifications-count-text ns-identity-stat-label">{unreadText}</span>
          </div>
        </div>

        <PushNotificationSettings lang={lang} />

        <div className="ns-notifications-toolbar">
          <div className="ns-notifications-filter" role="group" aria-label="Notification filters">
            <button
              type="button"
              className={filter === "all" ? "ns-notifications-chip is-active" : "ns-notifications-chip"}
              onClick={() => setFilter("all")}
            >
              {t.showAll}
            </button>

            <button
              type="button"
              className={filter === "unread" ? "ns-notifications-chip is-active" : "ns-notifications-chip"}
              onClick={() => setFilter("unread")}
            >
              {t.showUnread}
            </button>
          </div>

          <div className="ns-notifications-actions">
            <button type="button" className="ns-button ns-button-outline" onClick={loadNotifications} disabled={busy}>
              {busy ? t.refreshing : t.refresh}
            </button>

            <button type="button" className="ns-button ns-button-primary" onClick={markAllRead} disabled={busy || unreadCount === 0}>
              {t.markAllRead}
            </button>
          </div>
        </div>

        {err ? (
          <div className="ns-notifications-alert ns-notifications-alert-error" role="alert">
            {err}
          </div>
        ) : null}

        {notice ? (
          <div className="ns-notifications-alert ns-notifications-alert-success" role="status">
            {notice}
          </div>
        ) : null}

        <div className="ns-notifications-list">
          {busy && items.length === 0 ? (
            <div className="ns-notifications-state ns-notifications-loading">
              <span className="ns-notifications-state-icon">◷</span>
              <span>{t.loading}</span>
            </div>
          ) : null}

          {!busy && items.length === 0 ? (
            <div className="ns-notifications-state ns-notifications-empty">
              <span className="ns-notifications-state-icon">✓</span>
              <div>
                <strong>{filter === "unread" ? t.noUnread : t.notification}</strong>
                <p>{filter === "unread" ? t.noUnread : t.empty}</p>
              </div>
            </div>
          ) : null}

          {items.map((item) => {
            const isUnread = !item?.isRead;
            const when = formatWhen(item.createdAt, t, lang);

            return (
              <article key={item.id} className={isUnread ? "ns-notification-card is-unread" : "ns-notification-card"}>
                <div className="ns-notification-main">
                  <div className="ns-notification-icon" aria-hidden="true">
                    {notificationIcon(item.type)}
                  </div>

                  <div className="ns-notification-content">
                    <div className="ns-notification-meta-row">
                      <span className="ns-notification-type">{typeLabel(item.type, t)}</span>
                      {isUnread ? <span className="ns-notification-new">{t.newLabel}</span> : null}
                      {when ? <span className="ns-notification-time">{when}</span> : null}
                    </div>

                    <h3 className="ns-notification-heading">{titleLabel(item, t)}</h3>
                    <p className="ns-notification-message">{messageLabel(item, t)}</p>

                    {item.storyTitle ? <div className="ns-notification-story">{item.storyTitle}</div> : null}
                  </div>
                </div>

                <div className="ns-notification-card-actions">
                  {item.link ? (
                    <button type="button" className="ns-button ns-button-outline" onClick={() => openNotification(item)}>
                      {t.open}
                    </button>
                  ) : null}

                  {isUnread ? (
                    <button type="button" className="ns-button ns-button-outline" onClick={() => markOneRead(item.id)}>
                      {t.markRead}
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
